import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { mediaQueryUp } from 'components/utils/breakpoint'
import AccommodationPackageSelectionOptions from './AccommodationPackageSelectionOptions'
import AccommodationPackageSelectionTile from './AccommodationPackageSelectionTile'
import AccommodationPackagePriceSkeleton from './AccommodationPackagePriceSkeleton'

const Placeholder = styled(AccommodationPackageSelectionTile)`
  min-height: ${rem(360)};
  ${mediaQueryUp.desktop} { min-height: ${rem(320)}; }

  @media (prefers-reduced-motion: reduce) {
    *::after { animation: none; }
  }
`
const Details = styled.div`
  display: grid;
  align-content: start;
  gap: ${rem(12)};
`
const Inclusions = styled.div`
  display: grid;
  gap: ${rem(8)};
  padding-block: ${rem(8)};
`
const Actions = styled.div`
  display: grid;
  align-content: start;
  justify-items: start;
  gap: ${rem(8)};
`

/** Uses the actual card regions and carousel, so the loading surface shares their geometry. */
function AccommodationPackageSelectionSkeleton({ packageCount = 1 }: { packageCount?: number }) {
  const intl = useIntl()
  const placeholders = useMemo(() => Array.from({ length: packageCount }, (_, index) => index), [packageCount])
  return <div
    role="status"
    aria-label={intl.formatMessage({
      id: 'mfckBzPJ012zJpOOHHm3',
      defaultMessage: 'Checking availability',
      description: 'Accessible loading status while current hotel room packages and their prices are being retrieved',
    })}
    data-testid="package-selection-skeleton"
  >
    <AccommodationPackageSelectionOptions>
      {placeholders.map(index => <Placeholder
        key={index}
        details={<Details aria-hidden="true">
          <TextLoadingBox typography="body-large" width="80%" />
          <TextLoadingBox typography="caption-medium" width="60%" />
          <Inclusions>
            <TextLoadingBox typography="caption-medium" width="95%" />
            <TextLoadingBox typography="caption-medium" width="85%" />
            <TextLoadingBox typography="caption-medium" width="90%" />
            <TextLoadingBox typography="caption-medium" width="70%" />
          </Inclusions>
          <TextLoadingBox typography="caption-medium" width="65%" />
        </Details>}
        pricing={<AccommodationPackagePriceSkeleton />}
        actions={<Actions aria-hidden="true">
          <TextLoadingBox typography="caption-medium" width="5ch" />
          <LoadingBox width={100} height={32} borderRadius="round" />
        </Actions>}
      />)}
    </AccommodationPackageSelectionOptions>
  </div>
}

export default AccommodationPackageSelectionSkeleton
